import api from './api';

export interface Inventory {
  _id: string;
  itemName: string;
  itemCode: string;
  category: string;
  description?: string;
  unitPrice: number;
  purchasePrice?: number;
  minSalePrice?: number;
  unit: string;
  currency?: string;
  availableQuantity: number;
  minOrderQuantity?: number;
  maxOrderQuantity?: number;
  leadTimeDays?: number;
  status: 'active' | 'inactive' | 'out_of_stock';
  supplierId: string | { _id: string; name: string; email: string };
  images?: string[];
  specifications?: Record<string, any>;
  tags?: string[];
  sku?: string;
  manufacturer?: string;
  brandName?: string;
  enableLotTracking?: boolean;
  lots?: any[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateInventoryData {
  itemName: string;
  itemCode: string;
  category: string;
  description?: string;
  unitPrice: number;
  purchasePrice?: number;
  minSalePrice?: number;
  unit: string;
  currency?: string;
  availableQuantity: number;
  minOrderQuantity?: number;
  maxOrderQuantity?: number;
  leadTimeDays?: number;
  status?: 'active' | 'inactive' | 'out_of_stock';
  images?: string[];
  specifications?: Record<string, any>;
  tags?: string[];
  sku?: string;
  manufacturer?: string;
  brandName?: string;
  enableLotTracking?: boolean;
}

export interface LotData {
  lotNumber: string;
  quantity: number;
  manufacturingDate: string;
  expiryDate?: string;
  supplier?: string;
  batchNumber?: string;
  additionalInfo?: Record<string, any>;
}

export interface SearchInventoryParams {
  search?: string;
  category?: string;
  status?: string;
  supplierId?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  totalPages: number;
}

const inventoryService = {
  // Supplier endpoints
  create: (data: CreateInventoryData) =>
    api.post<Inventory>('/inventory', data).then((res) => res.data),

  getAll: (page: number = 1, limit: number = 20) =>
    api
      .get<PaginatedResponse<Inventory>>(`/inventory?page=${page}&limit=${limit}`)
      .then((res) => res.data),

  getById: (id: string) =>
    api.get<Inventory>(`/inventory/${id}`).then((res) => res.data),

  update: (id: string, data: Partial<CreateInventoryData>) =>
    api.patch<Inventory>(`/inventory/${id}`, data).then((res) => res.data),

  updateQuantity: (id: string, quantity: number) =>
    api.patch<Inventory>(`/inventory/${id}/quantity`, { quantity }).then((res) => res.data),

  delete: (id: string) =>
    api.delete(`/inventory/${id}`).then((res) => res.data),

  getCategories: () =>
    api.get<string[]>('/inventory/categories').then((res) => res.data),

  // Search endpoint (available for both buyers and suppliers)
  search: (params: SearchInventoryParams) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.category) queryParams.append('category', params.category);
    if (params.status) queryParams.append('status', params.status);
    if (params.supplierId) queryParams.append('supplierId', params.supplierId);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    return api
      .get<PaginatedResponse<Inventory>>(`/inventory/search?${queryParams.toString()}`)
      .then((res) => res.data);
  },

  // Lot management endpoints
  getLots: (inventoryId: string, includeInactive: boolean = false) =>
    api
      .get(`/inventory/${inventoryId}/lots?includeInactive=${includeInactive}`)
      .then((res) => res.data),

  addLot: (inventoryId: string, lotData: LotData) =>
    api.post(`/inventory/${inventoryId}/lots`, lotData).then((res) => res.data),

  updateLotQuantity: (inventoryId: string, lotNumber: string, quantity: number) =>
    api
      .patch(`/inventory/${inventoryId}/lots/${lotNumber}/quantity`, { quantity })
      .then((res) => res.data),

  deactivateLot: (inventoryId: string, lotNumber: string) =>
    api.delete(`/inventory/${inventoryId}/lots/${lotNumber}`).then((res) => res.data),

  getExpiringLots: (withinDays: number = 30) =>
    api.get(`/inventory/lots/expiring?withinDays=${withinDays}`).then((res) => res.data),
};

export default inventoryService;
